import { Component } from "@theme/component";

const SWIPE_THRESHOLD = 50;
const SCROLL_MODE_BREAKPOINT = 1024;

/**
 * Product Highlight Slider
 * Mobile: swipe between products.
 * Desktop: hover shows arrow cursor (left/right), click to change slide.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} slidesContainer
 * @property {HTMLElement} [imageArea]
 * @property {HTMLElement} [imageScrollContainer]
 * @property {HTMLElement} [slidesTrack]
 *
 * @extends {Component<Refs>}
 */
export class ProductHighlightSlider extends Component {
  /** @type {number} */
  #currentIndex = 0;

  /** @type {number} */
  #slideCount = 1;

  /** @type {number | null} */
  #touchStartX = null;

  /** @type {boolean} */
  #hoverLeft = false;

  /** @type {MediaQueryList | null} */
  #mediaQuery = null;

  /** @type {(() => void) | null} */
  #mediaQueryListener = null;

  /** @type {NodeListOf<Element> | null} */
  #thumbnails = null;

  /** @type {NodeListOf<Element> | null} */
  #lines = null;

  /** @type {NodeListOf<Element> | null} */
  #contentPanels = null;

  /** @type {NodeListOf<Element> | null} */
  #quickShopPanels = null;

  /** @type {string} */
  #cursorPrevStyle = "";

  /** @type {string} */
  #cursorNextStyle = "";

  /** @type {boolean} */
  #rafPending = false;

  get slideCount() {
    return parseInt(this.dataset.slideCount ?? "1", 10);
  }

  get #cursorPrevUrl() {
    return this.dataset.cursorPrev ?? "/cursor-prev.svg";
  }

  get #cursorNextUrl() {
    return this.dataset.cursorNext ?? "/cursor-next.svg";
  }

  static get isTouchDevice() {
    return typeof window !== "undefined" && "ontouchstart" in window;
  }

  /** @returns {boolean} */
  get #isScrollMode() {
    return this.#mediaQuery?.matches ?? false;
  }

  connectedCallback() {
    try {
      super.connectedCallback();
    } catch {
      return;
    }
    this.#slideCount = Math.max(0, this.slideCount);
    if (this.#slideCount > 1) {
      requestAnimationFrame(() => {
        try {
          this.#initialize();
        } catch {
          // Avoid breaking the page if refs or DOM differ from expected
        }
      });
    }
  }

  disconnectedCallback() {
    try {
      this.#destroy();
    } catch {
      /* ignore */
    }
    super.disconnectedCallback();
  }

  updatedCallback() {
    try {
      super.updatedCallback();
    } catch {
      /* ignore */
    }
    this.#slideCount = Math.max(0, this.slideCount);
    if (this.#slideCount > 1) {
      try {
        this.#applyMode();
        this.#showSlide(this.#currentIndex);
      } catch {
        /* ignore */
      }
    }
  }

  #initialize() {
    if (typeof window !== "undefined") {
      this.#mediaQuery = window.matchMedia(`(max-width: ${SCROLL_MODE_BREAKPOINT}px)`);
      this.#mediaQueryListener = () => this.#applyMode();
      this.#mediaQuery.addEventListener("change", this.#mediaQueryListener);
    }

    this.#cursorPrevStyle = `url("${this.#cursorPrevUrl}") 12 12, w-resize`;
    this.#cursorNextStyle = `url("${this.#cursorNextUrl}") 12 12, e-resize`;

    this.#thumbnails = this.querySelectorAll(".product-highlight__thumb");
    this.#lines = this.querySelectorAll(".product-highlight__line");
    this.#contentPanels = this.querySelectorAll(".product-highlight__content-panel");
    this.#quickShopPanels = this.querySelectorAll(".product-highlight__quick-shop-panel");

    const track = this.refs?.slidesTrack;
    if (track && this.#slideCount > 1) {
      track.style.transition = `transform 500ms cubic-bezier(0.25, 0.1, 0.25, 1)`;
      track.style.width = `${this.#slideCount * 100}%`;
      const slidePercent = 100 / this.#slideCount;
      for (const slide of track.children) {
        if (slide instanceof HTMLElement) {
          slide.style.flex = `0 0 ${slidePercent}%`;
        }
      }
    }
    this.#applyMode();
    this.#showSlide(0);
    this.#bindEvents();
  }

  #applyMode() {
    const imageScroll = this.refs?.imageScrollContainer;
    const track = this.refs?.slidesTrack;
    if (!track || this.#slideCount <= 1) return;

    const scrollMode = this.#isScrollMode;
    this.classList.toggle("product-highlight-slider--scroll-mode", scrollMode);

    if (scrollMode && imageScroll) {
      imageScroll.removeEventListener("scroll", this.#handleScroll, { passive: true });
      imageScroll.addEventListener("scroll", this.#handleScroll, { passive: true });
      const slideWidth = imageScroll.clientWidth;
      imageScroll.scrollLeft = this.#currentIndex * slideWidth;
      track.style.transform = "";
    } else {
      if (imageScroll) {
        imageScroll.removeEventListener("scroll", this.#handleScroll, { passive: true });
      }
      const percent = (this.#currentIndex * 100) / this.#slideCount;
      track.style.transform = `translateX(-${percent}%)`;
    }
  }

  #handleScroll = () => {
    const imageScroll = this.refs?.imageScrollContainer;
    if (!imageScroll || !this.#isScrollMode || this.#slideCount <= 1) return;
    const slideWidth = imageScroll.clientWidth;
    if (slideWidth <= 0) return;
    const index = Math.round(imageScroll.scrollLeft / slideWidth);
    const safeIndex = Math.max(0, Math.min(index, this.#slideCount - 1));
    if (safeIndex !== this.#currentIndex) {
      this.#currentIndex = safeIndex;
      this.#syncUI(safeIndex);
    }
  };

  #destroy() {
    const container = this.refs?.slidesContainer;
    const imageScroll = this.refs?.imageScrollContainer;
    if (!container || !this.isConnected) return;
    const cursorTarget = this.refs?.imageArea ?? container;
    try {
      if (imageScroll) {
        imageScroll.removeEventListener("scroll", this.#handleScroll, { passive: true });
      }
      container.removeEventListener("touchstart", this.#handleTouchStart, { passive: true });
      container.removeEventListener("touchend", this.#handleTouchEnd, { passive: true });
      cursorTarget.removeEventListener("mousemove", this.#handleMouseMove);
      cursorTarget.removeEventListener("mouseleave", this.#handleMouseLeave);
      cursorTarget.removeEventListener("click", this.#handleClick);
      this.querySelectorAll(".product-highlight__thumb").forEach((thumb) => {
        thumb.removeEventListener("click", this.#handleThumbClick);
      });
      cursorTarget.style.cursor = "";
    } catch {
      /* ignore */
    }
    if (this.#mediaQuery && this.#mediaQueryListener) {
      this.#mediaQuery.removeEventListener("change", this.#mediaQueryListener);
      this.#mediaQuery = null;
      this.#mediaQueryListener = null;
    }
    this.#thumbnails = null;
    this.#lines = null;
    this.#contentPanels = null;
    this.#quickShopPanels = null;
    this.classList.remove("product-highlight-slider--scroll-mode");
  }

  #bindEvents() {
    const container = this.refs?.slidesContainer;
    if (!container || !this.isConnected) return;
    container.addEventListener("touchstart", this.#handleTouchStart, { passive: true });
    container.addEventListener("touchend", this.#handleTouchEnd, { passive: true });

    const cursorTarget = this.refs?.imageArea ?? container;
    if (!ProductHighlightSlider.isTouchDevice) {
      cursorTarget.addEventListener("mousemove", this.#handleMouseMove);
      cursorTarget.addEventListener("mouseleave", this.#handleMouseLeave);
      cursorTarget.addEventListener("click", this.#handleClick);
    }

    this.querySelectorAll(".product-highlight__thumb").forEach((thumb) => {
      thumb.addEventListener("click", this.#handleThumbClick);
    });
  }

  #handleTouchStart = (event) => {
    const t = event.changedTouches?.[0];
    if (t) this.#touchStartX = t.screenX;
  };

  #handleTouchEnd = (event) => {
    if (this.#isScrollMode) return;
    if (this.#touchStartX === null) return;
    const t = event.changedTouches?.[0];
    if (!t) return;
    const diff = this.#touchStartX - t.screenX;
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff > 0) this.nextSlide();
      else this.prevSlide();
    }
    this.#touchStartX = null;
  };

  #handleMouseMove = (event) => {
    if (this.#rafPending) return;
    this.#rafPending = true;
    const clientX = event.clientX;
    const el = /** @type {HTMLElement} */ (event.currentTarget);
    requestAnimationFrame(() => {
      this.#rafPending = false;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      this.#hoverLeft = clientX - rect.left < rect.width / 2;
      el.style.cursor = this.#hoverLeft ? this.#cursorPrevStyle : this.#cursorNextStyle;
    });
  };

  #handleMouseLeave = (event) => {
    const el = event.currentTarget;
    if (el) el.style.cursor = "";
  };

  #handleClick = (event) => {
    if (event.target.closest("a, button")) return;
    if (this.#hoverLeft) this.prevSlide();
    else this.nextSlide();
  };

  #handleThumbClick = (event) => {
    const thumb = event.currentTarget;
    if (!thumb || !(thumb instanceof HTMLElement)) return;
    const raw = thumb.dataset.slideIndex;
    const index = raw != null ? parseInt(raw, 10) : 0;
    if (!Number.isNaN(index)) this.goToSlide({ index });
  };

  /**
   * Updates thumbnails, lines, content panels and aria from active index (no transform/scroll).
   * @param {number} safeIndex
   */
  #syncUI(safeIndex) {
    const track = this.refs?.slidesTrack;
    const slides = track ? Array.from(track.children).filter((el) => el instanceof HTMLElement) : [];
    slides.forEach((slide, i) => {
      if (slide instanceof HTMLElement) {
        slide.setAttribute("aria-hidden", i !== safeIndex ? "true" : "false");
      }
    });
    this.#thumbnails?.forEach((thumb, i) => {
      if (thumb instanceof HTMLElement) {
        const thumbIndex = thumb.dataset.slideIndex != null ? parseInt(thumb.dataset.slideIndex, 10) : i;
        const active = thumbIndex === safeIndex;
        thumb.classList.toggle("product-highlight__thumb--active", active);
        thumb.setAttribute("aria-current", active ? "true" : "false");
      }
    });
    this.#lines?.forEach((line, i) => {
      if (line instanceof HTMLElement) {
        line.classList.toggle("product-highlight__line--active", i === safeIndex);
      }
    });
    this.#contentPanels?.forEach((panel) => {
      if (panel instanceof HTMLElement) {
        const panelIndex = parseInt(panel.dataset.slideIndex ?? "-1", 10);
        const active = panelIndex === safeIndex;
        panel.classList.toggle("product-highlight__content-panel--active", active);
        panel.setAttribute("aria-hidden", active ? "false" : "true");
      }
    });
    this.#quickShopPanels?.forEach((panel) => {
      if (panel instanceof HTMLElement) {
        const panelIndex = parseInt(panel.dataset.slideIndex ?? "-1", 10);
        const active = panelIndex === safeIndex;
        panel.classList.toggle("product-highlight__quick-shop-panel--active", active);
        panel.setAttribute("aria-hidden", active ? "false" : "true");
      }
    });
  }

  #showSlide(index) {
    const container = this.refs?.slidesContainer;
    const track = this.refs?.slidesTrack;
    if (!container || !this.isConnected) return;

    const slides = track
      ? Array.from(track.children).filter((el) => el instanceof HTMLElement)
      : Array.from(container.children).filter((el) => el instanceof HTMLElement);
    const safeIndex = Math.max(0, Math.min(index, slides.length - 1));

    const imageScroll = this.refs?.imageScrollContainer;
    if (this.#isScrollMode && imageScroll) {
      const slideWidth = imageScroll.clientWidth;
      imageScroll.scrollLeft = safeIndex * slideWidth;
    } else if (track && this.#slideCount > 1) {
      const percent = (safeIndex * 100) / this.#slideCount;
      track.style.transform = `translateX(-${percent}%)`;
    }

    this.#syncUI(safeIndex);
    this.#currentIndex = safeIndex;
  }

  goToSlide(data) {
    const index = Number(data?.index ?? 0);
    const max = Math.max(0, this.#slideCount - 1);
    const clamped = Math.max(0, Math.min(index, max));
    this.#showSlide(clamped);
  }

  nextSlide() {
    const next = (this.#currentIndex + 1) % this.#slideCount;
    this.#showSlide(next);
  }

  prevSlide() {
    const prev = (this.#currentIndex - 1 + this.#slideCount) % this.#slideCount;
    this.#showSlide(prev);
  }
}

if (!customElements.get("product-highlight-slider")) {
  customElements.define("product-highlight-slider", ProductHighlightSlider);
}
